import { useAuthUser } from "@/context/auth/AuthContext";
import {
  getFeatureFromRoute,
  isAlwaysAvailableRoute,
} from "@/utils/planFeatureMapping";

interface PlanAccessResult {
  hasAccess: boolean;
  reason?:
    | "plan_feature"
    | "plan_status"
    | "no_plan"
    | "trial_expired"
    | "no_features";
  isPlanActive: boolean;
  isInTrial: boolean;
  requiredFeature?: string;
}

/**
 * Hook para validar el acceso a una ruta según el plan de negocio del usuario
 *
 * Valida:
 * 1. Si el plan está activo
 * 2. Si está en período de prueba (14 días gratis)
 * 3. Si el feature requerido está incluido en el plan
 *
 * @param pathname - Ruta a validar (ej: "/inventario/productos")
 * @returns Objeto con información de acceso y razón de denegación si aplica
 */
export function usePlanAccess(
  pathname: string,
  featureOverride?: string | string[]
): PlanAccessResult {
  const user = useAuthUser();

  // Si no hay usuario, no tiene acceso
  if (!user) {
    return {
      hasAccess: false,
      reason: "no_plan",
      isPlanActive: false,
      isInTrial: false,
    };
  }

  // Rutas que siempre están disponibles independientemente del plan
  if (isAlwaysAvailableRoute(pathname)) {
    return {
      hasAccess: true,
      isPlanActive: true,
      isInTrial: false,
    };
  }

  // Obtener información del plan
  const planStatus = user.businessPlanStatus;
  // Entitlement por `modules` (única fuente tras eliminar `businessPlanFeatures`
  // del /me). Las rutas del `ROUTE_TO_FEATURE_MAP` son vocab de recurso (en
  // `modules`); las comerciales (`ai/chat`, `ecommerce/storefront`) hoy NO están en
  // `modules` → esas rutas se niegan hasta que el BFF las incluya.
  const planFeatures = user.modules || [];
  const planStartDate = user.businessPlanStartDate;
  const now = new Date();

  // Verificar si está en período de prueba (antes de businessPlanStartDate)
  const isInTrial = planStartDate ? now < new Date(planStartDate) : false;

  // Si el plan está activo, todo bien
  const isPlanActive = planStatus === "active";

  // Si el plan NO está activo, verificar si está en período de prueba
  if (!isPlanActive && !isInTrial) {
    return {
      hasAccess: false,
      reason: "trial_expired",
      isPlanActive: false,
      isInTrial: false,
    };
  }

  // Si no hay features en el plan (caso raro), denegar acceso
  if (!planFeatures || planFeatures.length === 0) {
    return {
      hasAccess: false,
      reason: "no_features",
      isPlanActive,
      isInTrial,
    };
  }

  // Si hay override de feature, usarlo directamente
  if (featureOverride) {
    const requiredFeatures = Array.isArray(featureOverride)
      ? featureOverride
      : [featureOverride];
    const hasFeature = requiredFeatures.some((f) => planFeatures.includes(f));

    return {
      hasAccess: hasFeature,
      reason: hasFeature ? undefined : "plan_feature",
      isPlanActive,
      isInTrial,
      requiredFeature: requiredFeatures.join(", "),
    };
  }

  // Obtener el feature requerido para esta ruta
  const requiredFeature = getFeatureFromRoute(pathname);

  // Si no hay mapeo, permitir acceso (backward compatibility)
  if (!requiredFeature) {
    return {
      hasAccess: true,
      isPlanActive,
      isInTrial,
    };
  }

  // Verificar si el plan incluye este feature
  const hasFeature = planFeatures.includes(requiredFeature);

  return {
    hasAccess: hasFeature,
    reason: hasFeature ? undefined : "plan_feature",
    isPlanActive,
    isInTrial,
    requiredFeature,
  };
}
